import { IonIcon, IonSpinner, useIonToast } from '@ionic/react'
import { useQueryClient } from '@tanstack/react-query'
import {
	checkmarkCircleOutline,
	enter,
	helpCircleOutline,
	informationCircleOutline,
} from 'ionicons/icons'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../components/actions/Button'
import useUser from '../../../contexts/hooks/useUser'
import { ModalContext } from '../../../contexts/ModalContext'
import { useAuthorizations } from '../../../hooks/useAuthorizations'
import PageWrapper from '../../../wrappers/pageWrapper/PageWrapper'

const Authorizations: React.FC = () => {
	const { insertModal } = useContext(ModalContext)
	const { isCustomer } = useUser()

	const [loading, setLoading] = useState(false)
	const queryClient = useQueryClient()

	const [presentToast] = useIonToast()
	const { t } = useTranslation('pages_authorizations')

	const { data, isFetching, refetch } = useAuthorizations()

	const handleClick = () => {
		insertModal('dhInstructions')
	}

	const handleRetry = () => {
		setLoading(true)
		refetch()
			.then(result => {
				const found = result.data?.Agreements ?? []
				if (found.length > 0) {
					queryClient.invalidateQueries({ queryKey: ['objectChanges'] })

					presentToast({
						message: t('authorizationFound'),
						duration: 3000,
						color: 'success',
					})
				} else {
					presentToast({
						message: t('eiValtuutusta'),
						duration: 3000,
						color: 'danger',
					})
				}
			})
			.catch(() => {
				presentToast({
					message: t('error'),
					duration: 3000,
					color: 'danger',
				})
			})
			.finally(() => setLoading(false))
	}

	return (
		<PageWrapper page='authorizations' appBarOptions={{ backPath: '/settings' }}>
			<div className='text-center mb-5'>
				<h2 className='text-2xl font-bold text-text-dark dark:text-white'>{t('pageTitle')}</h2>
			</div>

			<div className='flex flex-col gap-3 text-sm'>
				{isCustomer && (
					<div className='rounded-xl bg-green-500/10 border border-green-500/25 p-4 flex flex-col gap-1'>
						<div className='flex flex-row items-center gap-2 text-green-600 dark:text-green-400 font-semibold mb-1'>
							<IonIcon icon={checkmarkCircleOutline} className='text-base shrink-0' />
							<span>{t('customerInfoTitle')}</span>
						</div>
						<p className='text-text-grey-200 dark:text-white/70 leading-relaxed'>
							{t('customerInfoBody')}
						</p>
					</div>
				)}

				{!isCustomer && (
					<div className='rounded-xl bg-white/10 dark:bg-white/5 border border-white/20 p-4 flex flex-col gap-1'>
						<div className='flex flex-row items-center gap-2 text-text-dark dark:text-white font-semibold mb-1'>
							<IonIcon icon={informationCircleOutline} className='text-base shrink-0' />
							<span>{t('nonCustomerTitle')}</span>
						</div>
						<p className='text-text-grey-200 dark:text-white/70 leading-relaxed'>
							{t('nonCustomerBody')}
						</p>
						<p className='mt-2 text-xs text-text-grey-200/60 dark:text-white/40 leading-relaxed'>
							{t('fingridNote')}
						</p>
					</div>
				)}
			</div>

			{!isCustomer && data?.Agreements && (
				<div className='mt-4 rounded-xl bg-white/10 dark:bg-white/5 border border-white/20 p-4 text-sm'>
					<p className='text-xs font-semibold uppercase tracking-widest text-text-grey-200/50 dark:text-white/40 mb-3'>
						{t('authorizationStatus')}
					</p>
					<p className='font-semibold text-text-dark dark:text-white'>
						{data?.GivenName} {data?.FamilyName}
					</p>
					<p className='mt-2'>
						{isFetching ? (
							<IonSpinner className='inline-block h-4 w-4' />
						) : (
							<span
								className={
									data?.Agreements.length > 0
										? 'text-green-500 font-semibold'
										: 'text-red-400 font-semibold'
								}
							>
								{data?.Agreements.length > 0
									? t('authorizationsActive', { count: data?.Agreements.length })
									: t('authorizationsNotFound')}
							</span>
						)}
					</p>
					{!isFetching && data?.Agreements.length === 0 && (
						<button
							onClick={handleRetry}
							disabled={loading}
							className='mt-3 w-full flex flex-row items-center justify-center gap-2 rounded-lg border border-brand-primary-100-light/40 dark:border-brand-secondary-100-dark/40 py-2 px-3 text-sm font-medium text-brand-primary-100-light dark:text-brand-secondary-100-dark hover:bg-brand-primary-100-light/10 dark:hover:bg-brand-secondary-100-dark/10 transition-colors disabled:opacity-50'
						>
							{loading ? (
								<IonSpinner className='h-4 w-4 text-brand-primary-100-light dark:text-brand-secondary-100-dark' />
							) : (
								<IonIcon icon={checkmarkCircleOutline} className='text-base shrink-0' />
							)}
							{t('yritaUudelleen')}
						</button>
					)}
				</div>
			)}

			{!isCustomer && (
				<div className='mt-5 flex flex-col gap-2'>
					<Button className='m-0' href='https://oma.datahub.fi/#/login'>
						<div className='flex flex-row items-center justify-center gap-2'>
							<IonIcon icon={enter} /> {t('jatka')}
						</div>
					</Button>
					<Button className='m-0' onClick={handleClick}>
						<div className='flex flex-row items-center justify-center gap-2'>
							<IonIcon icon={helpCircleOutline} /> {t('lisatietoja')}
						</div>
					</Button>
				</div>
			)}
		</PageWrapper>
	)
}

export default Authorizations
